import React, { Fragment, useCallback, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'gatsby-plugin-react-intl';
import { useFormik } from 'formik';
import { Button, Typography } from '@material-ui/core'; // FormControl

// Hooks
import { useDispatch } from '../store/StoreProvider';

// Components
import FormikInput from './FormikInput';

// Utils
import { isClient } from '../utils/utils';

const useStyles = makeStyles((theme) => ({
    inputSection: {
        width: '100%',
        display: 'flex',
        overflow: 'auto',
        justifyContent: 'space-between',
        paddingBottom: '20px',
    },
    inputsWrapper: {
        minWidth: '400px',
        marginRight: '15px',
    },
    sectionWrapper: {
        marginTop: '20px',
    },
    slideWrapper: () => {
        let height = 700;
        if (isClient()) {
            height = window.innerHeight - 240;
        }

        return {
            // maxWidth: '75%',
            maxHeight: `${height}px`,
            overflowY: 'auto',
        };
    },
}));

const Form = ({
    years = [],
    initialValues = {},
}) => {
    const intl = useIntl();
    const classes = useStyles();
    const dispatch = useDispatch();
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);

    const formik = useFormik({
        // initialValues: testValues || {},
        enableReinitialize: true,
        initialValues,
        onSubmit: (values) => {
            // TODO
        },
        validate: (values, props) => {
            // TODO
        },
    });

    const onIframeLoad = useCallback(() => {
        // https://stackoverflow.com/a/8558731
        if (isFormSubmitted) {
            // setSnackbarMessage('your_comment_submitted', SUCCESS_MESSAGE_TYPE);
            window.location = window.location.href;
        }
    }, [isFormSubmitted]);

    const onFormSubmit = useCallback(() => {
        setIsFormSubmitted(true);
    }, [setIsFormSubmitted]);

    const googleFormId = '1FAIpQLSfwAlROYKP26dDOBES3M0mW5VljeI1MMv04yBeIYzO8oo189g';

    return (
        <Fragment>
            <div className={classes.slideWrapper}>
                <iframe
                    title="hidden_iframe"
                    name="hidden_iframe"
                    id="hidden_iframe"
                    style={{ display: 'none' }}
                    onLoad={onIframeLoad}
                />
                <div className={classes.sectionWrapper}>
                    <Typography
                        color="textPrimary"
                        variant="h4"
                    >
                        {intl.formatMessage({ id: 'builder.details' })}
                    </Typography>
                    <div className={classes.inputSection}>
                        <div
                            className={classes.inputsWrapper}
                        >
                            <FormikInput
                                rows={1}
                                onChange={formik.handleChange}
                                name="name"
                                value={formik.values?.name}
                                error={(formik.errors?.name)}
                            />
                            <FormikInput
                                rows={1}
                                onChange={formik.handleChange}
                                name="email"
                                value={formik.values?.email}
                                error={(formik.errors?.email)}
                            />
                            <FormikInput
                                rows={1}
                                onChange={formik.handleChange}
                                name="phone"
                                value={formik.values?.phone}
                                error={(formik.errors?.phone)}
                            />
                        </div>
                    </div>
                </div>
                <div className={classes.sectionWrapper}>
                    <Typography
                        color="textPrimary"
                        variant="h4"
                    >
                        {intl.formatMessage({ id: 'builder.revenue' })}
                    </Typography>
                    <div className={classes.inputSection}>
                        {years.map((year, idx) => (
                            <div
                                key={year}
                                className={classes.inputsWrapper}
                            >
                                <Typography
                                    color="textPrimary"
                                    variant="h5"
                                >
                                    {year}
                                </Typography>
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.numberOfRooms`}
                                    value={formik.values?.[year]?.numberOfRooms}
                                    error={(formik.errors?.[year]?.numberOfRooms)}
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.annualAverageOccupancyRate`}
                                    value={formik.values?.[year]?.annualAverageOccupancyRate}
                                    error={(formik.errors?.[year]?.annualAverageOccupancyRate)}
                                    type="percentage"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.annualAveragePricePerRoom`}
                                    value={formik.values?.[year]?.annualAveragePricePerRoom}
                                    error={(formik.errors?.[year]?.annualAveragePricePerRoom)}
                                    type="percentage"
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className={classes.sectionWrapper}>
                    <Typography
                        color="textPrimary"
                        variant="h4"
                    >
                        {intl.formatMessage({ id: 'builder.expenses' })}
                    </Typography>
                    <div className={classes.inputSection}>
                        {years.map((year, idx) => (
                            <div
                                key={year}
                                className={classes.inputsWrapper}
                            >
                                <Typography
                                    color="textPrimary"
                                    variant="h5"
                                >
                                    {year}
                                </Typography>
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.waterAndSewage`}
                                    value={formik.values?.[year]?.waterAndSewage}
                                    error={(formik.errors?.[year]?.waterAndSewage)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.gas`}
                                    value={formik.values?.[year]?.gas}
                                    error={(formik.errors?.[year]?.gas)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.electricity`}
                                    value={formik.values?.[year]?.electricity}
                                    error={(formik.errors?.[year]?.electricity)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.landlinePhone`}
                                    value={formik.values?.[year]?.landlinePhone}
                                    error={(formik.errors?.[year]?.landlinePhone)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.cellphone`}
                                    value={formik.values?.[year]?.cellphone}
                                    error={(formik.errors?.[year]?.cellphone)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.internet`}
                                    value={formik.values?.[year]?.internet}
                                    error={(formik.errors?.[year]?.internet)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.cableTv`}
                                    value={formik.values?.[year]?.cableTv}
                                    error={(formik.errors?.[year]?.cableTv)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.insurance`}
                                    value={formik.values?.[year]?.insurance}
                                    error={(formik.errors?.[year]?.insurance)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.security`}
                                    value={formik.values?.[year]?.security}
                                    error={(formik.errors?.[year]?.security)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.accountingServices`}
                                    value={formik.values?.[year]?.accountingServices}
                                    error={(formik.errors?.[year]?.accountingServices)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.lawServices`}
                                    value={formik.values?.[year]?.lawServices}
                                    error={(formik.errors?.[year]?.lawServices)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.handlingAgentServices`}
                                    value={formik.values?.[year]?.handlingAgentServices}
                                    error={(formik.errors?.[year]?.handlingAgentServices)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.iptu`}
                                    value={formik.values?.[year]?.iptu}
                                    error={(formik.errors?.[year]?.iptu)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.otherFees`}
                                    value={formik.values?.[year]?.otherFees}
                                    error={(formik.errors?.[year]?.otherFees)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.marketingServices`}
                                    value={formik.values?.[year]?.marketingServices}
                                    error={(formik.errors?.[year]?.marketingServices)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.softwareServices`}
                                    value={formik.values?.[year]?.softwareServices}
                                    error={(formik.errors?.[year]?.softwareServices)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.onlineSalesCommission`}
                                    value={formik.values?.[year]?.onlineSalesCommission}
                                    error={(formik.errors?.[year]?.onlineSalesCommission)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.otherSalesCommission`}
                                    value={formik.values?.[year]?.otherSalesCommission}
                                    error={(formik.errors?.[year]?.otherSalesCommission)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.laundry`}
                                    value={formik.values?.[year]?.laundry}
                                    error={(formik.errors?.[year]?.laundry)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.bakery`}
                                    value={formik.values?.[year]?.bakery}
                                    error={(formik.errors?.[year]?.bakery)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.supermarket`}
                                    value={formik.values?.[year]?.supermarket}
                                    error={(formik.errors?.[year]?.supermarket)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.wages`}
                                    value={formik.values?.[year]?.wages}
                                    error={(formik.errors?.[year]?.wages)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.generalMaintenance`}
                                    value={formik.values?.[year]?.generalMaintenance}
                                    error={(formik.errors?.[year]?.generalMaintenance)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.otherExpenses`}
                                    value={formik.values?.[year]?.otherExpenses}
                                    error={(formik.errors?.[year]?.otherExpenses)}
                                    type="currency"
                                />
                            </div>
                        ))}
                    </div>
                </div>
                <div className={classes.sectionWrapper}>
                    <Typography
                        color="textPrimary"
                        variant="h4"
                    >
                        {intl.formatMessage({ id: 'builder.variables' })}
                    </Typography>
                    <div className={classes.inputSection}>
                        {years.map((year, idx) => (
                            <div
                                key={year}
                                className={classes.inputsWrapper}
                            >
                                <Typography
                                    color="textPrimary"
                                    variant="h5"
                                >
                                    {year}
                                </Typography>
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.occupancyRateIncreaseYoY`}
                                    value={formik.values?.[year]?.occupancyRateIncreaseYoY}
                                    error={(formik.errors?.[year]?.occupancyRateIncreaseYoY)}
                                    type="percentage"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.pricePerRoomIncreaseYoY`}
                                    value={formik.values?.[year]?.pricePerRoomIncreaseYoY}
                                    error={(formik.errors?.[year]?.pricePerRoomIncreaseYoY)}
                                    type="currency"
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.taxStructure`}
                                    value={formik.values?.[year]?.taxStructure}
                                    error={(formik.errors?.[year]?.taxStructure)}
                                    options={['mei', 'other']}
                                />
                                <FormikInput
                                    rows={1}
                                    onChange={formik.handleChange}
                                    name={`${year}.averageSalesCommissionRate`}
                                    value={formik.values?.[year]?.averageSalesCommissionRate}
                                    error={(formik.errors?.[year]?.averageSalesCommissionRate)}
                                    type="percentage"
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <form
                action={`https://docs.google.com/forms/d/e/${googleFormId}/formResponse`}
                onSubmit={onFormSubmit}
                target="hidden_iframe"
                method="post"
            >
                <input
                    name="entry.1483962846"
                    type="text"
                    style={{ display: 'none' }}
                    value={JSON.stringify(formik.values)}
                />
                <Button
                    // onClick={() => console.log(formik.values)}
                    variant="contained"
                    color="primary"
                    type="submit"
                >
                    Submit
                </Button>
            </form>
        </Fragment>
    );
};

export default Form;
